import React, { forwardRef } from 'react';
import styled from 'styled-components/macro';

interface ITextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  name: string;
  label?: string;
  value?: string;
  disabled?: boolean;
  errors?: string[];
  autoFocus?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
}

const TextArea = forwardRef<HTMLTextAreaElement, ITextAreaProps>(
  (
    { name, label, value, disabled, required, errors, autoFocus, onChange, onBlur, ...rest }: ITextAreaProps,
    ref,
  ): JSX.Element | null => {
    return (
      <Label>
        {label && <LabelText required={Boolean(required)}>{label}</LabelText>}
        <StyledTextArea
          name={name}
          ref={ref}
          value={value}
          className={errors?.length ? 'invalid' : ''}
          disabled={disabled}
          autoFocus={autoFocus}
          autoComplete="off"
          rows={3}
          aria-label={name}
          onChange={onChange}
          onBlur={onBlur}
          {...rest}
        />
        {errors?.length && <ErrorMessageContainer>{errors.map((error) => error)}</ErrorMessageContainer>}
      </Label>
    );
  },
);

TextArea.displayName = 'TextArea';

const Label = styled.label`
  display: flex;
  flex-direction: column;
  font-size: 14px;
`;

interface ILabelTextProps {
  required?: boolean;
}

const LabelText = styled.div<ILabelTextProps>`
  margin-bottom: 10px;

  font-size: 14px;
  font-weight: 600;

  &[required]:after {
    content: '*';
    color: var(--chakra-colors-text-danger);
    margin-left: 2px;
  }
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  border-radius: 7px;
  border: 1px solid hsl(0, 0%, 82%);
  padding: 9px 12px;
  font-size: 14px;
  line-height: 21px;
  background-color: var(--chakra-colors-background-default);
  color: inherit;
  transition: border-color 0.1s ease-in-out;
  font-family: inherit;

  &:hover:not([disabled]) {
    border-color: hsl(0, 0%, 70%);
  }

  &.invalid:not([disabled]) {
    border-color: #da4167;
  }

  &:focus:not([disabled]) {
    border-color: var(--chakra-colors-text-brand);
  }

  &[disabled] {
    background: #f3f3f3;
    opacity: 0.8;
  }
`;

const ErrorMessageContainer = styled.div`
  margin-top: 8px;
  color: var(--chakra-colors-text-danger);
  line-height: 1.5;
`;

export default TextArea;
